import { createContext, useEffect, useContext, useState } from "react";
import {
    auth,
    db
} from '../firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
} from 'firebase/auth'
import { setDoc, doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore'

const AuthContext = createContext(null)

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? savedUser : null;
    });

    // Modified signUp function to properly handle promises
    async function signUp(name, email, password) {
        try {
            // Wait for user creation
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);

            // Wait for document creation
            await setDoc(doc(db, 'users', email), {
                name: name,
                subscription_plan: "basic"
            });

            // Return the user credential
            return userCredential;
        } catch (error) {
            // Throw the error to be caught by the component
            throw error;
        }
    }

    function logIn(email, password) {
        localStorage.setItem('user', email);
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logOut() {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return signOut(auth)
    }

    function resetPassword(email) {
        return sendPasswordResetEmail(auth, email);
    }

    async function deleteAccount() {
        if (!user?.email) return;

        try {
            // Delete user data from Firestore
            const userRef = doc(db, 'users', user.email);
            await deleteDoc(userRef);

            // Delete user authentication account
            await user.delete();

            // Clear local storage
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            return true;
        } catch (error) {
            throw error;
        }
    }


    const contextValue = {
        signUp,
        logIn,
        logOut,
        deleteAccount,
        resetPassword, // Add the reset password function to the context
        user
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                try {
                    // Get user document from Firestore
                    const userRef = doc(db, 'users', currentUser.email);
                    const userDoc = await getDoc(userRef);
                    const userData = userDoc.data();

                    // Get or set initial emails remaining
                    let emailsRemaining = userData.emailsRemaining;
                    if (emailsRemaining === undefined) {
                        emailsRemaining = userData.subscription_plan === 'master' ? 40 :
                            userData.subscription_plan === 'pro' ? 20 :
                                userData.subscription_plan === 'basic' ? 10 : 0;

                        // Update Firestore with initial count
                        await updateDoc(userRef, {
                            emailsRemaining: emailsRemaining
                        });
                    }

                    const token = await currentUser.getIdToken();

                    // Create complete user object
                    const completeUser = {
                        ...currentUser,
                        ...userData,
                        emailsRemaining
                    };

                    // Update local state
                    setUser(completeUser);

                    // Send to extension
                    if (window.location.origin === 'https://apply-bridge.com') {
                        window.postMessage({
                            type: 'AUTH_STATE_CHANGE',
                            user: {
                                uid: currentUser.uid,
                                email: currentUser.email,
                                displayName: currentUser.displayName,
                                photoURL: currentUser.photoURL,
                                subscription_plan: userData.subscription_plan,
                                emailsRemaining: emailsRemaining
                            },
                            token
                        }, 'https://apply-bridge.com');
                    }
                } catch (error) {
                    console.error("Error fetching user data:", error);
                    setUser(null);
                }
            } else {
                setUser(null);
                if (window.location.origin === 'https://apply-bridge.com') {
                    window.postMessage({
                        type: 'AUTH_STATE_CHANGE',
                        user: null,
                        token: null
                    }, 'https://apply-bridge.com');
                }
            }
        });

        return () => unsubscribe();
    }, []);



    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext);